/**
 * The few UI pieces the team Agents page is built from, so every card looks the same: pills, buttons, a segmented choice, a switch,
 * a "⋯" row menu and a section card.
 */
import React, { useEffect, useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';

export const T = {
  surface: 'var(--lyra-color-surface)', surface2: 'var(--lyra-color-surface-2)', surface3: 'var(--app-surface-3)', border: 'var(--lyra-color-border)',
  ink: 'var(--lyra-color-text)', ink2: 'var(--app-text-2)', ink3: 'var(--lyra-color-text-muted)', ink4: 'var(--app-text-3)',
  accent: 'var(--lyra-color-primary)', accentLight: 'var(--app-primary-soft)', accentMid: 'var(--app-primary-soft-2)',
  danger: 'var(--lyra-color-danger)', dangerBg: 'var(--lyra-color-danger-bg)', ok: 'var(--lyra-color-success)',
};

export const btn = (kind = 'plain', extra = {}) => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, height: 32, padding: '0 13px', borderRadius: 9, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
  border: kind === 'primary' ? '1px solid transparent' : `1px solid ${kind === 'danger' ? T.danger : T.border}`,
  background: kind === 'primary' ? T.accent : T.surface,
  color: kind === 'primary' ? 'var(--app-on-accent)' : kind === 'danger' ? T.danger : T.ink, ...extra,
});

export const Pill = ({ tone = 'plain', children, title }) => (
  <span title={title} style={{
    display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 600, padding: '2px 9px', borderRadius: 99, lineHeight: 1.5, whiteSpace: 'nowrap',
    background: tone === 'bad' ? T.dangerBg : tone === 'accent' ? T.accentLight : tone === 'ok' ? 'var(--lyra-color-success-bg, var(--app-surface-3))' : T.surface2,
    color: tone === 'bad' ? T.danger : tone === 'accent' ? T.accent : tone === 'ok' ? T.ok : T.ink3,
    border: `1px solid ${tone === 'bad' ? 'transparent' : tone === 'accent' ? T.accentMid : T.border}`,
  }}>{children}</span>
);

/** A card with a title, a one-line explanation and optional actions on the right. */
export function Section({ title, hint, actions, children, tone }) {
  return (
    <section style={{ border: `1px solid ${tone === 'accent' ? T.accentMid : T.border}`, background: tone === 'accent' ? T.accentLight : T.surface, borderRadius: 16, overflow: 'hidden' }}>
      <header style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '16px 20px 12px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: T.ink, letterSpacing: '-0.01em' }}>{title}</h3>
          {hint && <p style={{ margin: '3px 0 0', fontSize: 12.5, color: T.ink3, lineHeight: 1.5 }}>{hint}</p>}
        </div>
        {actions && <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>{actions}</div>}
      </header>
      <div>{children}</div>
    </section>
  );
}

/** Two or three mutually exclusive choices side by side. An option can be disabled with a reason shown on hover. */
export function Seg({ value, options, onChange, disabled, label }) {
  return (
    <div role="radiogroup" aria-label={label} style={{ display: 'inline-flex', border: `1px solid ${T.border}`, borderRadius: 9, overflow: 'hidden', background: T.surface2 }}>
      {options.map((o, i) => {
        const on = o.value === value;
        const off = disabled || o.disabled;
        return (
          <button key={o.value} type="button" role="radio" aria-checked={on} disabled={off && !on} title={o.title}
            onClick={() => { if (!on && !off) onChange(o.value); }}
            style={{ height: 30, padding: '0 11px', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', border: 'none', borderLeft: i ? `1px solid ${T.border}` : 'none', cursor: on ? 'default' : off ? 'not-allowed' : 'pointer',
              background: on ? T.accentLight : 'transparent', color: on ? T.accent : off ? T.ink4 : T.ink2, opacity: off && !on ? 0.6 : 1 }}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function Switch({ on, onChange, disabled, label }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled} onClick={() => onChange(!on)}
      style={{ position: 'relative', width: 36, height: 20, borderRadius: 99, border: 'none', padding: 0, cursor: disabled ? 'default' : 'pointer', background: on ? T.ok : T.border, opacity: disabled ? 0.6 : 1, transition: 'background .15s' }}>
      <span style={{ position: 'absolute', top: 2, left: on ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: 'var(--lyra-color-surface)', boxShadow: '0 1px 2px rgba(0,0,0,.25)', transition: 'left .15s' }} />
    </button>
  );
}

/** A "⋯" button with a small menu of actions. Items: [{ label, onClick, danger, disabled, hint }]. Nothing renders when there are none. */
export function RowMenu({ items, label = 'More actions' }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  const shown = items.filter(Boolean);
  if (!shown.length) return null;
  return (
    <span ref={box} style={{ position: 'relative', display: 'inline-flex' }}>
      <button type="button" aria-label={label} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)}
        style={{ ...btn('plain'), width: 32, padding: 0, background: open ? T.surface3 : T.surface }}><MoreHorizontal size={16} /></button>
      {open && (
        <div role="menu" style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', minWidth: 236, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, boxShadow: '0 12px 30px rgba(0,0,0,0.14)', padding: 5, zIndex: 40 }}>
          {shown.map((it) => (
            <button key={it.label} type="button" role="menuitem" disabled={it.disabled} title={it.hint}
              onClick={() => { setOpen(false); it.onClick(); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', border: 'none', background: 'none', borderRadius: 8, fontSize: 12.5, fontFamily: 'inherit', cursor: it.disabled ? 'not-allowed' : 'pointer', color: it.danger ? T.danger : T.ink, opacity: it.disabled ? 0.5 : 1 }}
              onMouseEnter={(e) => { if (!it.disabled) e.currentTarget.style.background = T.surface2; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}>
              {it.label}
              {it.hint && <span style={{ display: 'block', fontSize: 11, color: T.ink4, marginTop: 1 }}>{it.hint}</span>}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

/** "12,331" → "12.3k" for tight places. */
export const kTokens = (n) => { const v = Number(n) || 0; return v >= 100000 ? `${Math.round(v / 1000)}k` : v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v); };

/** "2 min ago", "3 h ago", "12 Oct". */
export function ago(date, now = Date.now()) {
  const t = new Date(date).getTime();
  if (!t) return '';
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  if (s < 7 * 86400) return `${Math.round(s / 86400)} d ago`;
  return new Date(t).toLocaleDateString([], { day: 'numeric', month: 'short' });
}
